import { useApolloClient } from '@apollo/client';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { CommentTreeNode, getUserCommentTree } from './commentHelpers';
import { formatResource } from '../../common/helpers/localization/contentResourceHelper';
import commonResource from '../../locales/th/common.json';
import { UserComment } from '../../queries/contentPage/commentList';
import { Post } from '../../queries/contentPage/postDetail';
import { fetchComments } from '../../services/comment';
import { WholeState as CommonState, WholeState } from '../../store/common/type';
import { CommentAlert } from './CommentAlert';
import {
  CommentContentWrapper,
  CommentWrapper,
  ShowMoreComment,
  TitleWrapper,
} from './CommentContainer.style';
import CommentForm from './CommentForm';
import { CommentFormInput } from './CommentFormInput';
import CommentItem from './CommentItem';
import { SignedInUserWrapper } from './CommentItem.style';
import { useCommentAlertMessage } from './useCommentMessage';
import { getArticleTypeInGA } from '../../common/analytics/helpers/articleTypeHelper';
import logger from '../../common/helpers/logger/logger';
import {
  trackSubmittedArticleComment,
  trackSubmittedProjectComment,
} from '../../common/analytics/analyticsTracker';
import { AnalyticsProject } from '../../common/analytics/interfaces/analyticsProject';

interface CommentContainerProps {
  postId: number;
  source: string;
  project?: AnalyticsProject;
  postData?: Post;
  isOnProjectPage: boolean;
}

const CommentContainer: React.FC<CommentContainerProps> = ({
  postId,
  source,
  project,
  isOnProjectPage,
  postData,
}) => {
  const client = useApolloClient();
  const [comments, setComments] = useState<UserComment[]>([]);
  const commentListItems: CommentTreeNode[] = getUserCommentTree(comments);
  const [visible, setVisible] = useState(3);
  const [isShowForm, setShowForm] = useState(false);

  const userName = useSelector<CommonState, string>((state) =>
    state.common !== undefined ? state.common.displayName : '',
  );
  const email = useSelector<CommonState, string>((state) =>
    state.common !== undefined ? state.common.loginEmail : '',
  );
  const userInfo = useSelector((state: WholeState) =>
    state !== undefined ? state.common.user : {},
  );
  const { alertMessage, showAlertMessage } = useCommentAlertMessage();

  useEffect(() => {
    setComments([]);
    fetchComments(client, { postId: postId })
      .then((data) => {
        setComments(data.commentList || []);
      })
      .catch(() => {
        logger.logError(
          'ComponentContainer',
          'Failed to fetch the post comments.',
        );
      });
  }, [client, postId]);

  const showMoreComment = () => {
    setVisible((visible) => visible + 3);
  };

  const onNewCommentAdded = (comment: UserComment) => {
    setComments([comment, ...comments]);

    if (isOnProjectPage) {
      trackSubmittedProjectComment(userInfo, project, source);
    } else if (postData) {
      trackSubmittedArticleComment(
        userInfo,
        {
          ...postData,
          articleTypeInGA: getArticleTypeInGA(
            postData.postCategory,
            postData.videoType,
          ),
        },
        postData.project,
        source,
      );
    }
  };

  return (
    <CommentWrapper data-test={'comment'} isOnProjectPage={isOnProjectPage}>
      <TitleWrapper data-test="comment-title">
        {comments.length} {commonResource.comment.title}
      </TitleWrapper>
      {alertMessage && (
        <CommentAlert data-test="comment-alert-msg" message={alertMessage} />
      )}
      {userName && (
        <SignedInUserWrapper data-test="comment-sign-in-info">
          {commonResource.comment.signedInAs + ' ' + userName}
        </SignedInUserWrapper>
      )}
      {isShowForm ? (
        <CommentForm
          profile={userName && email ? { userName, email } : null}
          onFinish={(comment) => {
            onNewCommentAdded(comment);
            setShowForm(false);
          }}
          onError={(errorCode) => showAlertMessage(errorCode)}
          parentCommentId={0}
          postId={postId}
        />
      ) : (
        <CommentFormInput
          placeholder={commonResource.comment.joinTheDiscussion}
          onFocus={() => setShowForm(true)}
          data-test="comment-placeholder-input"
        />
      )}
      {commentListItems?.length > 0 && (
        <>
          <CommentContentWrapper>
            {commentListItems.slice(0, visible).map((item) => {
              return (
                <CommentItem
                  key={item.id}
                  comment={item}
                  onNewCommentAdded={onNewCommentAdded}
                  postId={postId}
                />
              );
            })}
          </CommentContentWrapper>
          {visible < commentListItems.length && (
            <ShowMoreComment
              onClick={showMoreComment}
              data-test="show-more-comment"
            >
              {formatResource(commonResource.comment.viewComment, {
                numberOfCommentLeft: String(commentListItems.length - visible),
              })}
            </ShowMoreComment>
          )}
        </>
      )}
    </CommentWrapper>
  );
};

export default CommentContainer;
